import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useSettings } from '@/store/settings';
import { useQueryClient } from '@tanstack/react-query';
import { graphifyApi } from '@/api/graphify';
import { fetchPhysicalGraphRaw } from '@/hooks/useGraphify';
import { useContentFreshness } from '@/hooks/useContentFreshness';
import TopNavigation from '@/components/navigation/TopNavigation';
import MobileTabBar from '@/components/navigation/MobileTabBar';
import { useIsMobile, MOBILE_QUERY } from '@/hooks/useIsMobile';
import SubMenuPanel from '@/components/navigation/SubMenuPanel';
import Sidebar from '@/components/navigation/Sidebar';
import FusionSearch from '@/components/search/FusionSearch';
import ChatInputBar from '@/components/navigation/ChatInputBar';
import ModelManagerBanner from '@/components/settings/ModelManagerBanner';

import AnnouncementBanner from '@/components/common/AnnouncementBanner';
import AndroidDownloadEntry from '@/components/common/AndroidDownload';
import OfflineStatusBar from '@/components/sync/OfflineStatusBar';
import { bootOfflineOutbox } from '@/utils/offlineOutbox';
import LoginModal from '@/components/auth/LoginModal';
import MascotWidget from '@/components/mascot/MascotWidget';

const SIDEBAR_MIN = 220;
const SIDEBAR_MAX = 480;
const SIDEBAR_WIDTH_KEY = 'psb-sidebar-width';

// 桌面端（Electron）判定：桌面端内不显示桌面端下载入口
const isDesktop = !!window.psbDesktop?.isDesktop;

const AppLayout: FC = () => {
  // 手机窄屏（<768px）：桌面壳的固定侧栏把内容区挤到不可用（390px 实测只剩约 110px，
  // /ingest/notes 有 823 个 <32px 可点元素）——故手机端侧栏改覆盖式抽屉（默认收起），
  // 主模块落到常驻底部标签栏（MobileTabBar）。
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window === 'undefined' || window.matchMedia?.(MOBILE_QUERY).matches !== true
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  // 侧边栏宽度可拖拽（右缘拉条），宽度持久化到 localStorage
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
    return saved >= SIDEBAR_MIN && saved <= SIDEBAR_MAX ? saved : 280;
  });
  const draggingRef = useRef(false);
  const { isLoggedIn, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isWorkbench = location.pathname === '/workbench';
  // 工作台浅色态判定（与工作台页同口径）：侧边栏拉条按钮要随主题，别浅底还挂黑按钮
  const wbTheme = useSettings((s) => s.theme);
  const wbLight = isWorkbench && (wbTheme === 'system'
    ? (window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false) === false
    : wbTheme === 'light');

  // 工作台默认收起侧边栏（仅此页面，10-06 拍板）：进入记住原态并收起，离开恢复
  const savedSidebarRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (location.pathname !== '/workbench') return;
    setSidebarOpen((prev) => { savedSidebarRef.current = prev; return false; });
    return () => {
      if (savedSidebarRef.current !== null) setSidebarOpen(savedSidebarRef.current);
      savedSidebarRef.current = null;
    };
  }, [location.pathname]);
  // B+A 准实时刷新：后台写入（同步/打标/自进化）改库后版本戳轮询触发失效
  useContentFreshness();

  // 登录后后台预热全部图谱（10-08 拍板：启动帐号即后台加载好，进图谱页/工作台零等待）：
  // 物理图（trim/full 同键共享）+ 语义图 + 构建状态；物理图大 payload 服务端 SWR
  // 水位变了也先回旧产物，预热只触发后台重算不阻塞
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isLoggedIn) return;
    void queryClient.prefetchQuery({
      queryKey: ['graphify-physical-graph'],
      queryFn: fetchPhysicalGraphRaw,
      staleTime: 30 * 60 * 1000,
      retry: false,
    }).catch(() => undefined);
    void queryClient.prefetchQuery({
      queryKey: ['graphify-graph', 'main'],
      queryFn: async () => (await graphifyApi.getGraph()).data,
      staleTime: 60 * 1000,
      retry: false, // 未构建 404 是正常态
    }).catch(() => undefined);
    void queryClient.prefetchQuery({
      queryKey: ['graphify-status'],
      queryFn: async () => (await graphifyApi.getStatus()).data,
      retry: false,
    }).catch(() => undefined);
  }, [isLoggedIn, queryClient]);

  const startDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    const onMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return;
      setSidebarWidth(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, ev.clientX)));
    };
    const onUp = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setSidebarWidth((w) => {
        localStorage.setItem(SIDEBAR_WIDTH_KEY, String(w));
        return w;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // 桌面端全局快捷键（Ctrl+Shift+N）：唤起快记
  useEffect(() => {
    return window.psbDesktop?.onQuickNote?.(() => navigate('/ingest/notes/new'));
  }, [navigate]);

  // 离线队列重放（B5）：online 事件 + 30s 轮询 + 启动即一轮
  useEffect(() => {
    bootOfflineOutbox();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="w-8 h-8 border-2 border-info border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-bg-primary relative">
      {/* Fusion Search Modal (Cmd+K quick search) */}
      <FusionSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* System Announcement */}
      <AnnouncementBanner />


      {/* Top Navigation Bar */}
      <header className="flex-shrink-0 z-40 bg-bg-secondary border-b border-border-light overflow-visible">
        <TopNavigation
          onLoginClick={() => setLoginOpen(true)}
          onToggleSidebar={() => setMobileNavOpen((v) => !v)}
          isMobile={isMobile}
        />
      </header>

      {/* 离线状态条（B5）：断网/待重放/冲突待处理时显示 */}
      <OfflineStatusBar />

      {/* Guest notice */}
      {!isLoggedIn && (
        <div className="flex-shrink-0 z-30 bg-bg-secondary border-b border-border-light px-4 py-2 flex items-center justify-between gap-4">
          <p className="text-xs text-text-secondary">
            你当前以游客身份浏览，登录后可使用全部功能。
          </p>
          <button
            onClick={() => setLoginOpen(true)}
            className="px-3 py-1 rounded-[2px] border border-accent/40 text-accent hover:bg-accent/10 text-xs font-medium transition-colors shrink-0"
          >
            登录 / 注册
          </button>
        </div>
      )}

      {/* Sub Menu Panel (slides down when active) */}
      <SubMenuPanel />

      {/* Main Layout: Sidebar + Content */}
      <div className="flex flex-1 overflow-hidden relative z-10">
        {/* Left Sidebar：桌面=常驻（可拖宽）；手机=覆盖式抽屉（默认收起，底部栏「更多」开） */}
        {isMobile ? (
          <AnimatePresence>
            {mobileNavOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setMobileNavOpen(false)}
                  className="fixed inset-0 z-40 bg-black/55"
                />
                <motion.aside
                  initial={{ x: '-100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '-100%' }}
                  transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                  className="fixed top-0 bottom-0 left-0 z-50 w-[84%] max-w-[320px] bg-bg-secondary border-r border-border-light overflow-y-auto"
                >
                  <Sidebar />
                </motion.aside>
              </>
            )}
          </AnimatePresence>
        ) : (
        <>
        <AnimatePresence>
          {sidebarOpen && (
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: sidebarWidth, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={draggingRef.current ? { duration: 0 } : { duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="flex-shrink-0 bg-bg-secondary border-r border-border-light overflow-y-auto"
              style={{ width: sidebarWidth }}
            >
              <Sidebar />
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Sidebar 拖拽拉条（调宽度；与折叠按钮共存） */}
        {sidebarOpen && (
          <div
            onMouseDown={startDrag}
            className="absolute top-0 bottom-0 w-1.5 cursor-col-resize z-30 hover:bg-info/30 transition-colors"
            style={{ left: sidebarWidth - 3 }}
            title="拖拽调整侧边栏宽度"
          />
        )}

        {/* Sidebar Toggle（工作台页随主题：深态黑按钮浅箭头/浅态白按钮墨箭头，其余页保持 glass） */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={`absolute left-0 top-1/2 -translate-y-1/2 z-30 p-1.5 rounded-r-[2px] transition-colors
            ${isWorkbench
              ? wbLight
                ? 'bg-white/85 border border-black/10 border-l-0 hover:bg-white'
                : 'bg-[#0a0b0d]/90 border border-white/15 border-l-0 hover:bg-[#17181c]'
              : 'glass hover:bg-bg-hover'}`}
          style={{ marginLeft: sidebarOpen ? sidebarWidth : 0 }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`transition-transform ${sidebarOpen ? 'rotate-180' : ''} ${isWorkbench ? (wbLight ? 'text-black/60' : 'text-white/70') : 'text-info'}`}
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        </>
        )}

        {/* Main Content Area */}
        <main className="flex-1 overflow-hidden relative flex flex-col">
          {/* 「回工作台」浮钮（10-07 五拍）：来源页是工作台才挂（跳转时 location.state.from
              打标）——回退用 history back，工作台从 sessionStorage 恢复刚刚出去的视图 */}
          {(location.state as { from?: string } | null)?.from === 'workbench' && (
            <button
              onClick={() => navigate(-1)}
              className="fixed bottom-4 left-4 z-40 flex items-center gap-1.5 rounded-full border border-border-light bg-bg-secondary/95 backdrop-blur px-3.5 py-1.5 text-[11.5px] text-text-secondary hover:text-text-primary hover:bg-bg-hover shadow-lg transition-colors"
              title="回工作台（恢复刚刚出去的视图）"
            >
              <ArrowLeft size={12} /> 回工作台
            </button>
          )}
          <div className="flex-1 overflow-auto flex flex-col pb-[calc(52px+env(safe-area-inset-bottom))] md:pb-0">
            {/* 内容至少撑满一屏：底栏被顶到视口外，只有拉到最底才出现（不占常驻 chrome）。
                必须用 flex-1 拿确定性高度——min-h-full 只有 min-height，子级 h-full
                会解析成 auto，GraphLayout 等 h-full 页面高度全塌（09-05 实捕） */}
            <div className="flex-1 flex flex-col">
              <Outlet />
            </div>
            {/* 底部栏（09-06 扩容）：在内容列内部、随滚动出现；不再横贯侧边栏下方。
                链接组与落地页 footer 同口径（docs/学习指南/GitHub/sitemap/下载）。
                **桌面端也渲染**：右下角的安卓版入口在桌面端同样需要——桌面端用户
                正是最需要手机版的人（故不套 !isDesktop）；站内链接组仍仅云端显示。 */}
            <div className="px-6 py-3 border-t border-border-light flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-text-muted">
              <span className="shrink-0">Molore · 本地优先的个人知识库</span>
              {!isDesktop && (
                <nav aria-label="站点链接" className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <a href="/docs" target="_blank" rel="noreferrer" className="hover:text-text-primary transition-colors">使用文档</a>
                  <a href="/learn" className="hover:text-text-primary transition-colors">学习指南</a>
                  <a href="https://github.com/motuo520/grzhishiku" target="_blank" rel="noreferrer" className="hover:text-text-primary transition-colors">GitHub 开源</a>
                  <a href="/sitemap.xml" className="hover:text-text-primary transition-colors">站点地图</a>
                </nav>
              )}
              <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">
                {!isDesktop && (
                  <a
                    href="/download/PSB-Setup-0.2.188.exe"
                    className="hover:text-text-primary transition-colors"
                  >
                    桌面端（Windows）↓
                  </a>
                )}
                <AndroidDownloadEntry
                  compact
                  linkClassName="inline-flex items-center gap-1 hover:text-text-primary transition-colors"
                  qrClassName="inline-flex items-center gap-1 hover:text-text-primary transition-colors"
                />
              </div>
            </div>
          </div>
          {/* AI 助手（悬浮球 + 全局对话面板）：fixed 浮层不占布局，
              留在 main 列内仅为维持原组件挂载位置 */}
          <ChatInputBar sidebarOpen={sidebarOpen} onLoginClick={() => setLoginOpen(true)} />
        </main>
      </div>

      {/* 模型管家引导横幅 + 常驻模型预热（仅桌面端） */}
      <ModelManagerBanner />

      {/* Login modal: triggered by guest notice or protected actions */}
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />

      {/* Cute mascot assistant */}
      <MascotWidget />

      {/* 手机端底部主模块标签栏（10-18）：md 以上自动隐藏，桌面布局零影响 */}
      <MobileTabBar onMore={() => setMobileNavOpen(true)} />
    </div>
  );
};

export default AppLayout;
